import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { RESOLUTION_STATUS, beYearOptions, fmtDateTH } from './aoShared'

// ธุรการ: ติดตามมติที่ประชุมทุกการประชุม
export default function ResolutionTracking() {
  const [status, setStatus] = useState('')
  const [year, setYear] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      if (year) params.set('year', year)
      const res = await axiosInstance.get(`/api/ao/meetings/resolutions?${params.toString()}`)
      if (res.data.success) setRows(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [status, year])

  useEffect(() => { fetchData() }, [fetchData])

  const editResult = async (r) => {
    const { value: formValues } = await Swal.fire({
      title: 'อัปเดตผลการดำเนินการ',
      html: `
        <select id="sw-status" class="form-select form-select-sm mb-2">
          ${Object.entries(RESOLUTION_STATUS).map(([k, v]) =>
            `<option value="${k}" ${k === r.ms_status ? 'selected' : ''}>${v.label}</option>`).join('')}
        </select>
        <textarea id="sw-result" class="form-control form-control-sm" rows="3"
          placeholder="ผลการดำเนินการ">${r.ms_result || ''}</textarea>`,
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ปิด',
      preConfirm: () => ({
        ms_status: document.getElementById('sw-status').value,
        ms_result: document.getElementById('sw-result').value,
      }),
    })
    if (!formValues) return
    try {
      await axiosInstance.put(`/api/ao/resolutions/${r.ms_id}`, formValues)
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const today = new Date().toLocaleDateString('en-CA')

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-task-line me-2 text-primary" />
              ติดตามมติที่ประชุม
            </h4>
            <div className="d-flex gap-2">
              <Form.Select size="sm" style={{ width: 150 }} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">ทุกสถานะ</option>
                {Object.entries(RESOLUTION_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </Form.Select>
              <Form.Select size="sm" style={{ width: 120 }} value={year} onChange={e => setYear(e.target.value)}>
                <option value="">ทุกปี</option>
                {beYearOptions().map(y => <option key={y} value={y}>พ.ศ. {y}</option>)}
              </Form.Select>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <Card className="card-one">
              <Card.Body className="p-0">
                {rows.length === 0 ? (
                  <div className="text-center text-muted py-4">ไม่มีมติในเงื่อนไขที่เลือก</div>
                ) : (
                  <Table responsive hover className="mb-0 fs-sm">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 140 }}>การประชุม</th>
                        <th>มติ</th>
                        <th style={{ width: 140 }}>ผู้รับผิดชอบ</th>
                        <th style={{ width: 110 }}>กำหนดเสร็จ</th>
                        <th style={{ width: 140 }}>สถานะ</th>
                        <th style={{ width: 60 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => {
                        const rs = RESOLUTION_STATUS[r.ms_status] || RESOLUTION_STATUS.pending
                        const overdue = r.ms_due && r.ms_due < today && r.ms_status !== 'done'
                        return (
                          <tr key={r.ms_id}>
                            <td>
                              <Link to={`/admin-office/meetings/${r.mt_id}`}>ครั้งที่ {r.mt_full_no}</Link>
                              <div className="text-muted" style={{ fontSize: '0.72rem' }}>{fmtDateTH(r.mt_date)}</div>
                            </td>
                            <td>
                              <div>{r.ms_text}</div>
                              {r.ms_result && <div className="text-muted" style={{ fontSize: '0.75rem' }}>ผล: {r.ms_result}</div>}
                            </td>
                            <td>{r.owner_name || '-'}</td>
                            <td className={overdue ? 'text-danger fw-semibold' : ''}>
                              {fmtDateTH(r.ms_due)}
                              {overdue && <div style={{ fontSize: '0.72rem' }}>เลยกำหนด</div>}
                            </td>
                            <td><Badge bg={rs.bg}>{rs.label}</Badge></td>
                            <td className="text-end">
                              <Button size="sm" variant="outline-primary" title="อัปเดตผล" onClick={() => editResult(r)}>
                                <i className="ri-edit-line" />
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
      <Footer />
    </>
  )
}
